import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import api from "../../../api/axiosInstance";

const FeaturedCourses = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await api.get("/api/courses", {
          params: { isFeatured: true },
        });

        const mappedCourses = res.data.data.map((course) => ({
          id: course._id,
          title: course.title,
          description: course.description,
          image: course.imageUrl,
          price: course.priceUSD,
          duration: `${course.durationWeeks} weeks`,
          level: course.level,
          students: course.students ?? null,
          rating: course.rating,
          category: course.category,
        }));

        setCourses(mappedCourses);
      } catch (error) {
        console.error("Failed to fetch courses", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        Loading courses...
      </div>
    );
  }

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4">
        {/* HEADER */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
            <Icon name="BookOpen" size={16} />
            <span className="font-medium">Popular Courses</span>
          </div>

          <h2 className="text-3xl font-bold mb-3">
            Featured Interview Preparation Courses
          </h2>
          <p className="text-muted-foreground">
            Choose from our expertly designed courses
          </p>
        </div>

        {/* COURSES GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => (
            <div
              key={course.id}
              className="bg-card rounded-xl overflow-hidden shadow-md border border-border"
            >
              {/* IMAGE */}
              <div className="relative h-56">
                <img
                  src={course.image}
                  alt={course.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />

                <div className="absolute top-4 left-4">
                  <span className="bg-primary text-white px-3 py-1 rounded-full text-sm">
                    {course.category}
                  </span>
                </div>

                <div className="absolute top-4 right-4">
                  <span className="bg-white/90 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                    <Icon name="Star" size={14} />
                    {course.rating}
                  </span>
                </div>
              </div>

              {/* CONTENT */}
              <div className="p-5">
                <h3 className="text-xl font-bold mb-2">
                  {course.title}
                </h3>

                <p className="text-muted-foreground mb-4 line-clamp-3">
                  {course.description}
                </p>

                <div className="flex gap-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-1">
                    <Icon name="Clock" size={16} />
                    {course.duration}
                  </div>

                  <div className="flex items-center gap-1">
                    <Icon name="BarChart" size={16} />
                    {course.level}
                  </div>

                  <div className="flex items-center gap-1">
                    <Icon name="Users" size={16} />
                    {course.students
                      ? course.students.toLocaleString()
                      : "—"}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="text-2xl font-bold text-primary">
                    ${course.price}
                  </div>

                  <Button
                    size="sm"
                    onClick={() =>
                      navigate("/authentication-portal")
                    }
                  >
                    Enroll Now
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* FOOTER */}
        <div className="text-center mt-10">
          <Button
            variant="outline"
            onClick={() => navigate("/pricing-plans")}
          >
            View All Courses
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedCourses;
